import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { Calendar, Clock, Building2, Video, Plus, Search, Filter, ChevronDown, List, CalendarDays, Eye, Edit, Trash2, AlertCircle, CheckCircle, Loader, X, ChevronLeft, ChevronRight, Users, TrendingUp, FileText, Briefcase, Target, MessageSquare, Brain, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiRequest from '@/services/requests';
import Pagination from './components/Pagination';
import FilterSection from './components/FilterSection';
import StatsSection from './components/StatsSection';
import ListView from './components/ListView';
import CalendarView from './components/CalendarView';
import ScheduleModal from './components/ScheduleModal';
import PracticeModal from './components/PracticeModal';
import LoadingModal from './components/LoadingModal';
import { LoadingState, EmptyState } from './components/LoadingState';



const Interviews = () => {
const navigate = useNavigate();
const resultsPerPage = 10;
const [interviews, setInterviews] = useState([]);
const [applications, setApplications] = useState([]);
const [companyOptions, setCompanyOptions] = useState([]);
const [isLoading, setIsLoading] = useState(true);
const [viewMode, setViewMode] = useState('list');
const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [totalResults, setTotalResults] = useState(0);
const [showScheduleModal, setShowScheduleModal] = useState(false);
const [showPracticeModal, setShowPracticeModal] = useState(false);
const [showLoadingModal, setShowLoadingModal] = useState(false);
const [loadingProgress, setLoadingProgress] = useState(0);
const [showFilters, setShowFilters] = useState(false);
const [currentMonth, setCurrentMonth] = useState(new Date());
const [selectedInterview, setSelectedInterview] = useState(null);

const [stats, setStats] = useState({
total:0,
upcoming:0,
mock:0
});  
const [filters, setFilters] = useState({
search: '',
company: 'all',
dateFrom: '',
dateTo: '',
status: 'all',
type: 'all'
});
const [newInterview, setNewInterview] = useState({
type: 'real', // 'real' or 'mock'
applicationId: '',
customRole: '',
role: '',
date: '',
time: '',
interview_format:'',
meeting_link:'',
location:'',
notes:''
});




/* ---------- life-cycle ---------- */
useEffect(() => {
loadInterviews();                                
loadApplications();                               
}, [filters]);



/* ---------- data loaders ---------- */
const loadInterviews = async () => {                 
setIsLoading(true);
try {
const [listData, statData] = await Promise.all([
apiRequest.interviewsList({ ...filters, page: currentPage, perPage: resultsPerPage }),
apiRequest.interviewsStats()
]);
setInterviews(listData.interviews);
setTotalResults(listData.totalResults);
setTotalPages(listData.totalPages);
setStats(statData.stats);
} catch (e) {
console.error(e);
toast.error('Could not load interviews, please refresh or try again later!',{
position:'bottom-left'
});
} finally {
setIsLoading(false);
}
};


const loadApplications = async () => {               
try {
const { applications } = await apiRequest.applicationsList({ status: 'all', perPage: 100 });
setApplications(applications);
} catch (e) {
console.error(e);
// toast.error('Could not load applications');
}
};


const handlePageChange = (page) => {
setCurrentPage(page);
window.scrollTo({ top: 0, behavior: 'smooth' });
};


/* ---------- create interview ---------- */
const createInterview = async () => {  
setShowPracticeModal(false);
setShowScheduleModal(false);
setShowLoadingModal(true);
setLoadingProgress(0);

const messages = [
"Analyzing job requirements...",
"Researching company information...",
"Gathering industry insights...",
"Compiling interview questions...",
"Generating preparation document..."
];

for (let i = 0; i < messages.length; i++) {
await new Promise(r => setTimeout(r, 1500));
setLoadingProgress(((i + 1) / messages.length) * 100);
}

try {

if(newInterview.type === 'real'){
const selectedApp = applications.find(a => a.id === parseInt(newInterview.applicationId));

const payload = {
applicationId: newInterview.applicationId,
job_title: selectedApp?.jobTitle,
company_name: selectedApp?.company,
company_logo_url: selectedApp?.companyLogo,
scheduled_date: newInterview.date,
scheduled_time: newInterview.time,
interview_format:newInterview.interview_format,
meeting_link:newInterview.meeting_link,
location:newInterview.location,
interview_round:'Technical & Behavioral',
notes:'',
};

await apiRequest.interviewsCreate(payload);

}else{

const payload = {
job_title: newInterview.customRole || newInterview.role,
scheduled_date: newInterview.date,
scheduled_time: newInterview.time,
location:'AI Practice Room',
notes:'',
};

await apiRequest.createPracticeInterview(payload)

}



toast.success('Interview scheduled');
loadInterviews();                              // refresh list + stats
} catch (e) {
console.error(e);
toast.error('Scheduling failed');
} finally {

setShowLoadingModal(false);
setNewInterview({
type: 'real',
applicationId: '',
customRole: '',
role: '',
date: '',
time: '',
interview_format:'',
meeting_link:'',
location:'',
notes:''
});


}
};


// Suggested roles for mock interviews
const suggestedRoles = [
"Frontend Developer",
"Backend Developer",
"Full Stack Developer",
"DevOps Engineer",
"Data Scientist",
"Product Manager",
"UI/UX Designer"
];




const handleScheduleInterview = () => {
if (newInterview.type === 'real' && !newInterview.applicationId) {
alert('Please select an application');
return;
}
if (newInterview.type === 'mock' && !newInterview.role && !newInterview.customRole) {
alert('Please select or enter a role');
return;
}
if (!newInterview.date || !newInterview.time) {
alert('Please select date and time');
return;
}

if (newInterview.type === 'real') {
// Show practice suggestion modal
setShowScheduleModal(false);
setShowPracticeModal(true);
} else {
// For mock interviews, directly proceed with creation
createInterview();
}
};


const suggestPracticeTimes = () => {
if (!newInterview.date) return [];

const interviewDate = new Date(newInterview.date + 'T' + newInterview.time);
const suggestions = [];

// 3 days before
const threeDaysBefore = new Date(interviewDate);
threeDaysBefore.setDate(threeDaysBefore.getDate() - 3);
suggestions.push({
date: threeDaysBefore.toISOString().split('T')[0],
time: '10:00',
label: '3 days before - Morning'
});

// 2 days before
const twoDaysBefore = new Date(interviewDate);
twoDaysBefore.setDate(twoDaysBefore.getDate() - 2);
suggestions.push({
date: twoDaysBefore.toISOString().split('T')[0],
time: '14:00',
label: '2 days before - Afternoon'
});

// 1 day before
const oneDayBefore = new Date(interviewDate);
oneDayBefore.setDate(oneDayBefore.getDate() - 1);
suggestions.push({
date: oneDayBefore.toISOString().split('T')[0],
time: '16:00',
label: '1 day before - Evening'
});

return suggestions;
};

const getStatusConfig = (status) => {
const configs = {
scheduled: { label: 'scheduled', value:'scheduled', color: 'bg-yellow-100 text-yellow-800', icon: CalendarDays },
missed: { label: 'missed',value:'missed', color: 'bg-sky-100 text-sky-800', icon: Clock },
completed: { label: 'completed',value:'completed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
cancelled: { label: 'cancelled',value:'cancelled', color: 'bg-red-100 text-red-800', icon: X },
rescheduled: { label: 'rescheduled',value:'rescheduled', color: 'bg-orange-100 text-orange-800', icon: Clock }
};
return configs[status] || configs.scheduled;
};

const getDaysOfMonth = (date) => {
const year = date.getFullYear();
const month = date.getMonth();
const firstDay = new Date(year, month, 1);
const lastDay = new Date(year, month + 1, 0);
const daysInMonth = lastDay.getDate();
const startingDayOfWeek = firstDay.getDay();

const days = [];

// Add empty cells for days before month starts
for (let i = 0; i < startingDayOfWeek; i++) {
days.push(null);
}

// Add days of the month
for (let i = 1; i <= daysInMonth; i++) {
days.push(i);
}

return days;
};

const getInterviewsForDate = (day) => {
if (!day) return [];
const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
return interviews.filter(int => int.date === dateStr);
};

const handleViewDetails = (interview) => {
if (interview.type === 'mock') {
// Navigate to mock interview details page
navigate(`/mock-interview/${interview.id}`)
} else {
// Navigate to real interview details page
navigate(`/interview/${interview.id}`)
}
};



return (
<div className="min-h-screen bg-background">
<Header />

<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">

{/* Page Header */}
<div className="mb-8">
<div className="flex items-center justify-between mb-4">
<div>
<h1 className="text-xl lg:text-3xl font-bold text-text-primary mb-2">My Interviews</h1>
<p className="text-text-secondary text-sm">
Manage and prepare for your interviews
</p>
</div>

<div className="hidden md:flex items-center space-x-3">
<Button
variant="outline"
iconName="RefreshCw"
iconPosition="left"
iconSize={16}
onClick={loadInterviews}
disabled={isLoading}
>
Refresh
</Button>

<Button
variant="default"
iconName="Plus"
iconPosition="left"
iconSize={16}
onClick={() => setShowScheduleModal(true)}
>
Schedule Interview
</Button>
</div>
</div>


{/* Stats Cards */}
<StatsSection stats={stats}/>

</div>




{/* View Toggle and Filters */}
<FilterSection
filters={filters} 
setFilters={setFilters}
setShowFilters={setShowFilters}
showFilters={showFilters}
setViewMode={setViewMode}
viewMode={viewMode}
companyOptions={companyOptions}
/>


{/* Content Area */}
{viewMode === 'list' ? (

<ListView 
isLoading={isLoading}
interviews={interviews}
setShowScheduleModal={setShowScheduleModal}
getStatusConfig={getStatusConfig}
handleViewDetails={handleViewDetails}
/> 

) : (

<CalendarView 
currentMonth={currentMonth}
setCurrentMonth={setCurrentMonth}
getDaysOfMonth={getDaysOfMonth}
getInterviewsForDate={getInterviewsForDate}
handleViewDetails={handleViewDetails}
/>

)}



{/* Pagination */}
<Pagination
currentPage={currentPage}
totalPages={totalPages}
totalResults={totalResults}
resultsPerPage={resultsPerPage}
onPageChange={handlePageChange}
isLoading={isLoading}
/>



{/* Mobile Action Buttons */}
<div className="md:hidden fixed bottom-16 right-6 flex flex-col space-y-3">
<Button
variant="default"
size="icon"
className="w-10 h-10 rounded-full shadow-elevation-2"
onClick={()=>setShowScheduleModal(true)}
disabled={isLoading}
>
<Icon name="Plus" size={20} />
</Button>

<Button
variant="secondary"
size="icon"
className="w-10 h-10 rounded-full shadow-elevation-2"
onClick={loadInterviews}
disabled={isLoading}
>
<Icon name="RefreshCw" size={20} />
</Button>

</div>

</div>
</main>





{/* Schedule Interview Modal */}
{showScheduleModal && (

<ScheduleModal 
setShowScheduleModal={setShowScheduleModal}
setNewInterview={setNewInterview}
newInterview={newInterview}
applications={applications}
suggestedRoles={suggestedRoles}
handleScheduleInterview={handleScheduleInterview}
/>

)}

{/* Practice Session Modal */}
{showPracticeModal && (

<PracticeModal
suggestPracticeTimes={suggestPracticeTimes}
setNewInterview={setNewInterview}
newInterview={newInterview}
setShowPracticeModal={setShowPracticeModal}
setShowScheduleModal={setShowScheduleModal}
createInterview={createInterview} 
/>

)}

{/* Loading Modal */}
{showLoadingModal && (

<LoadingModal 
loadingProgress={loadingProgress}
/>

)}
</div>
);
};

export default Interviews;